import React, { FC } from 'react';
import { useParams } from 'next/navigation';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import Accordion from 'components/commercetools-ui/atoms/accordion';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { LinkReference, Reference } from 'types/reference';
import { FooterColumn, SocialMedia } from '.';
import Column from './atoms/column';

interface Props {
  logo?: ImageProps;
  socialMedia?: SocialMedia[];
  bottomRow?: FooterColumn;
  pageBottom?: string;
  pageBottomLink?: Reference;
}
interface LogoImageProps extends Omit<ImageProps, 'title'> {
  title?: Record<string, string>;
}

const FooterBottom: FC<Props> = ({ logo, bottomRow, pageBottom, pageBottomLink }) => {
  const { locale } = useParams();

  const resolvedTitle = (logo as LogoImageProps)?.title?.[locale.replace(/-/g, '_')] ?? 'Logo';
  return (
    <>
      <div>
        <div className="hidden items-center justify-between px-24 py-36 md:flex xl:px-48">
          {logo && (
            <div className="w-160">
              <Image {...logo} alt="logo" title={resolvedTitle} />
            </div>
          )}
          <ul className="flex flex-row gap-20 self-center">
            {bottomRow?.links?.map((item, i) => {
              const linkClassName = `group flex items-center${(item?.reference as LinkReference)?.link?.includes('javascript:void') ? ' ot-sdk-show-settings' : ''}`;
              return (
                <li key={i} className="w-fit">
                  <Link variant="primary" link={item.reference} className={linkClassName}>
                    <Typography as="fragment">{item.name}</Typography>
                    {item?.reference?.openInNewWindow && (
                      <ArrowTopRightOnSquareIcon className="ml-4 size-14 stroke-gray-400 group-hover:stroke-white" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex flex-col items-center md:hidden">
          {bottomRow?.links && bottomRow?.links.length > 0 && (
            <ul className="flex w-full flex-col">
              <li className="border-b border-neutral-300">
                <Accordion
                  variant="arrow"
                  closedSectionTitle={bottomRow?.header ?? ''}
                  openSectionTitle={bottomRow?.header}
                  buttonClassName="h-48 text-14 font-medium text-neutral-100 px-12 text-neutral-150"
                  iconClassName="text-neutral-100"
                >
                  <div className="mb-16 ml-20 flex flex-row text-14 md:ml-24">
                    <Column links={bottomRow?.links ?? []} className="px-0" />
                  </div>
                </Accordion>
              </li>
            </ul>
          )}
          {logo && (
            <div className="my-16 flex w-full flex-col items-center">
              <Image {...logo} alt="logo" />
            </div>
          )}
        </div>
      </div>
      <div className="w-full border-t border-secondary-grey" />
      <div className="flex items-center justify-start px-36 py-24 md:flex xl:px-48">
        <Link
          link={pageBottomLink}
          className="group text-14 text-neutral-500 hover:text-white hover:underline focus:underline active:underline"
        >
          <Typography as="fragment">{pageBottom}</Typography>
          {pageBottomLink?.openInNewWindow && (
            <ArrowTopRightOnSquareIcon className="mb-4 ml-2 inline-block size-14 stroke-gray-400 group-hover:stroke-white" />
          )}
        </Link>
      </div>
    </>
  );
};

export default FooterBottom;
